﻿<template>
  <div id="questionnaire">
    <div v-for="(question, index) in options" :key="index">
      <div
        v-if="question.IsActivated && (question.IsVisibleForPortal || alwaysShow)"
        :id="'question-' + getQuestionId(question, index)"
        :data-test-claim-event-date="maxDate"
        :data-test-answered="question.ChosenAnswer !== null ? 'true' : 'false'"
        :data-test-question-type="question.QuestionnaireType"
        :data-test-question-id="getQuestionId(question, index)"
        class="control"
      >
        <div :data-test="'questionnarie-question-' + index">
          <h3
            :for="'question-' + getQuestionId(question, index)"
            :id="'question-label-' + getQuestionId(question, index)"
            :class="{ 'text-question': !headingThree, h3: headingThree }"
            style="cursor: default"
          >
            {{ question.Title }}
            <tooltip :content="question.Tooltip" />
        </h3>

          <!-- Dropdown -->
          <Field
            v-if="question.QuestionnaireType === 0"
            v-slot="{ field }"
            :model-value="question.ChosenAnswer"
            :rules="'required'"
            :name="'question-' + getQuestionId(question, index)"
            :label="question.Title"
          >
            <VueSelect
              :model-value="question.ChosenAnswer"
              @update:modelValue="
                (newValue) => {
                  question.ChosenAnswer = newValue;
                  updateQuestionnaire(question, index);
                }
              "
              :id="'question-' + getQuestionId(question, index)"
              :name="'question-' + getQuestionId(question, index)"
              :options="question.AnswersList"
              :data-test-questionnaire-id="getQuestionId(question, index)"
              :reduce="(option) => option.Id"
              data-test-questionnaire-type="1"
              label="Value"
              :aria-labelledby="'question-label-' + getQuestionId(question, index)"
            />
          </Field>

          <!-- Text -->
          <Field
            v-if="question.QuestionnaireType === 1"
            :id="'question-' + getQuestionId(question, index)"
            :model-value="question.ChosenAnswer"
            @update:model-value="(newValue) => (question.ChosenAnswer = newValue)"
            :rules="'required'"
            :name="'question-' + getQuestionId(question, index)"
            :label="question.Title"
            :data-test-questionnaire-id="getQuestionId(question, index)"
            data-test-questionnaire-type="2"
            type="text"
            :aria-labelledby="'question-label-' + getQuestionId(question, index)"
            @blur="updateQuestionnaire(question, index)"
          />
      
          <!-- Date -->
          <Field
            v-if="question.QuestionnaireType === 2"
            :name="'question-' + getQuestionId(question, index)"
            v-slot="{ field }"
            rules="required"
            :label="question.Title"
            v-model="question.ChosenAnswer"
          >
            <date-picker
              :id="'question-id-' + getQuestionId(question, index)"
              v-bind="field"             
              :max-date="getMaxDate(question)"
              :key="question.LineId"
              :name="'question-' + getQuestionId(question, index)"
              :date="parseDate(question.ChosenAnswer)"
              :data-test-questionnaire-id="getQuestionId(question, index)"
              data-test-questionnaire-type="3"
              class="datepicker"
              display-format="DD-MM-YYYY"
              save-format="DD-MM-YYYY"
              :aria-labelledby="'question-label-' + getQuestionId(question, index)"
            />
          </Field>

          <!-- Time -->
          <Field
            v-if="question.QuestionnaireType === 3"
            v-slot="{ field }"
            :model-value="question.ChosenAnswer"
            @update:modelValue="
              (newValue) => {
                question.ChosenAnswer = newValue;
                updateQuestionnaire(question, index);
              }
            "
            :rules="'required'"
            :name="'question-' + getQuestionId(question, index)"
            :label="question.Title"
          >
            <VueSelect
              v-bind="field"
              :model-value="question.ChosenAnswer"
              :id="'question-' + getQuestionId(question, index)"
              :name="'question-' + getQuestionId(question, index)"
              :options="timeOptions"
              :data-test-questionnaire-id="getQuestionId(question, index)"
              data-test-questionnaire-type="4"
              start-value="12:00"
              :aria-labelledby="'question-label-' + getQuestionId(question, index)"
            />
          </Field>

          <!-- yes no -->
          <div v-if="question.QuestionnaireType === 4" class="row" role="radiogroup">
            <div v-for="(radio, i) in question.AnswersList" :key="i" class="small-6 columns">
              <div class="radio-stacked">
                <Field
                  :id="getQuestionId(question, i) + '-' + radio.Value"
                  v-model="question.ChosenAnswer"
                  :rules="'required'"
                  :name="'question-' + getQuestionId(question, i)"
                  :value="radio.Id"
                  :label="question.Title"
                  type="radio"
                  data-test-questionnaire-type="10002-jaNej"
                  @update:model-value="updateQuestionnaire(question, i)"
                />
                <label :for="getQuestionId(question, i) + '-' + radio.Value">{{
                  radio.Value
                }}</label>
              </div>
            </div>
          </div>

          <!-- radio -->
          <div v-if="question.QuestionnaireType === 5" role="radiogroup" :aria-labelledby="'question-label-' + getQuestionId(question, index)">
            <div class="row" :data-test-base-radio-button="'question-' + question.LineId">
              <div
                v-for="(radio, i) in question.AnswersList"
                :key="i"
                :class="[
                  'small-12',
                  mediumClass(question.AnswersList.length),
                  'columns',
                  'radio-inline',
                  'align-self-middle'
                ]"
              >
                <Field
                  :id="getQuestionId(question, i) + '-' + radio.Value"
                  v-model="question.ChosenAnswer"
                  :rules="'required'"
                  :name="'question-' + getQuestionId(question, i)"
                  :value="radio.Id"
                  :label="question.Title"
                  type="radio"
                  data-test-questionnaire-type="10002-radio"
                  @update:model-value="updateQuestionnaire(question, i)"
                />
                <label :for="getQuestionId(question, i) + '-' + radio.Value">{{
                  radio.Value
                }}</label>
              </div>
            </div>
          </div>

          <!-- RegEx -->
          <Field
            v-if="question.QuestionnaireType === 6"
            :id="'question-' + getQuestionId(question, index)"
            v-model="question.ChosenAnswer"
            :rules="{ required: true, regex: question.AnswerRegex }"
            :name="'question-' + getQuestionId(question, index)"
            :label="question.FormatErrorText ?? question.Title"
            :data-test-questionnaire-id="getQuestionId(question, index)"
            :data-test-regex="question.AnswerRegex"
            data-test-questionnaire-type="6"
            type="text"
            :aria-labelledby="'question-label-' + getQuestionId(question, index)"
            @update:model-value="updateQuestionnaire(question, index)"
          />

          <!-- radio pills -->
          <div v-if="question.QuestionnaireType === 7" role="radiogroup" :aria-labelledby="'question-label-' + getQuestionId(question, index)">
            <div class="row" :data-test-base-radio-button="'question-' + question.LineId">
              <div class="small-12 columns">
                <Field
                  v-model="question.ChosenAnswer"
                  :rules="'required'"
                  :name="'question-' + getQuestionId(question, index)"
                  :label="question.Title"
                  v-slot="{ field }"
                >
                  <base-radio-button-pills
                    :model-value="question.ChosenAnswer"
                    v-bind="field"
                    :options="getOptions(question.AnswersList)"
                    @update:model-value="updateQuestionnaire(question, index)"
                  />
                </Field>
              </div>
            </div>
          </div>

          <!-- radio vertical -->
          <div
            v-if="question.QuestionnaireType === 8"
            class="push-up"
            :data-test-questionnaire-id="getQuestionId(question, index)"
            role="radiogroup"
            :aria-labelledby="'question-label-' + getQuestionId(question, index)"
          >
            <div v-for="(radio, i) in question.AnswersList" :key="i" class="row">
              <div class="columns radio-inline">
                <Field
                  :id="getQuestionId(question, i) + '-' + radio.Value"
                  v-model="question.ChosenAnswer"
                  :rules="'required'"
                  :name="'question-' + getQuestionId(question, i)"
                  :value="radio.Id"
                  :label="question.Title"
                  :info-box-text="question.InfoBoxText"
                  type="radio"
                  data-test-questionnaire-type="8"
                  @update:model-value="updateQuestionnaire(question, i)"
                />
                <label :for="getQuestionId(question, i) + '-' + radio.Value">
                  {{ radio.Value }}
                </label>
              </div>
            </div>
          </div>

          <info-box
            v-if="question.HasInfoBoxText"
            :text="question.InfoBoxText"
            :arrow-show="true"
          />

          <div v-for="(answer, i) in question.AnswersList" :key="i">
            <info-box 
              v-if="answer.HasInfoBoxText && answer.Id === question.ChosenAnswer"
              :text="answer.InfoBoxText"
              :arrow-show="false"
            />
          </div>

          <ValidationError
            v-if="question.QuestionnaireType !== 6"
            :name="'question-' + getQuestionId(question, index)"
            :data-test="'questionnaire-validation-' + getQuestionId(question, index)"
          />

          <ValidationError
            v-if="question.QuestionnaireType === 6 && question.RegexValue !== null"
            :name="'question-' + getQuestionId(question, index)"
            :overrides="{ regex: question.RegexValue }"
            :data-test="'questionnaire-validation-' + getQuestionId(question, index)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import Tooltip from '@/components/UI/Tooltip.vue';
import timeOptions from '@/common/TimeOptions.js';
import DatePicker from '@/components/UI/Pickadate.vue';
import InfoBox from '@/components/UI/InfoBox.vue';
import BaseRadioButtonPills from '@/components/Forms/BaseRadioButtonPills.vue';
import { Field } from 'vee-validate';
import ValidationError from '@/components/UI/ValidationError.vue';

export default {
  name: 'Questionnaire',
  components: {
    DatePicker,
    Tooltip,
    ValidationError,
    BaseRadioButtonPills,
    InfoBox,
    Field
  },
  emits: ['questionnaire-updated'],
  props: {
    options: {
      type: Array,
      default() {
        return [];
      }
    },
    maxDate: {
      type: [Object, String],
      default() {
        return moment();
      }
    },
    alwaysShow: {
      type: Boolean,
      default() {
        return false;
      }
    },
    headingThree: {
      type: Boolean,
      default() {
        return false;
      }
    }
  },
  data() {
    return {
      timeOptions,
      isBlocked: false
    };
  },
  computed: {
    chosenAnswers() {
      let a;
      const o = this.options;
      for (var i in o) {
        a.push({
          Id: o[i].LineId,
          ChosenAnswer: o[i].ChosenAnswer
        });
      }
      return a;
    }
  },
  methods: {
    getMaxDate(question) {
      if (question.HasMaxDateToday) 
        return new Date();
      return null;
    },
    getOptions(answersList) {
      var result = answersList.map((answer) => ({ label: answer.Value, value: answer.Id }));
      return result;
    },
    getQuestionId(question, index) {
      if (question.LineId === 0) {
        return index;
      }
      return question.LineId;
    },
    updateQuestionnaire(question, index) {
      this.isBlocked = false;

      // iterate over all questions
      for (var i in this.options) {
        const currentQuestion = this.options[i];
        const dependencies = currentQuestion.DependenciesList;

        // if a question has no dependencies, skip it
        if (dependencies == undefined) {
          continue;
        }

        // set initial IsActivated status
        let showQuestion = currentQuestion.IsActivated;
        let currentDependencyHasChosenAnswer = false;

        // check dependencies (if any) and modify IsActivated as needed
        for (var dependencyCount in dependencies) {
          const currentDependency = dependencies[dependencyCount];

          // get the current dependency question itself
          const currentConstraintQuestion = this.options.find(
            (element) => element.LineId === currentDependency.ConstraintQuestionId
          );

          // check if the required answer of the dependency question matches the actual chosen answer on that question
          currentDependencyHasChosenAnswer =
            currentDependency.ConstraintAnswerId === currentConstraintQuestion.ChosenAnswer;

          // handle multiple dependencies
          if (dependencyCount > 0) {
            // or
            if (currentDependency.OperatorId === 10002) {
              showQuestion = showQuestion || currentDependencyHasChosenAnswer;
            }

            // and
            if (currentDependency.OperatorId === 10001) {
              showQuestion = showQuestion && currentDependencyHasChosenAnswer;
            }
          } else {
            showQuestion = currentDependencyHasChosenAnswer;
          }
        }

        // Activate or deactivate question
        if (showQuestion) {
          currentQuestion.IsActivated = true;
        } else {
          currentQuestion.IsActivated = false;

          if (!currentQuestion.IsReadOnly) {
            currentQuestion.ChosenAnswer = null;
          }
        }

        this.checkIfQuestionAnswerIsBlocking(currentQuestion);
      }
      this.emitUpdate(question, index);
    },
    parseDate(date) {
      if (date === null) {
        return null;
      }

      const splitDate = date.split('-');
      const day = parseInt(splitDate[0]);
      const month = parseInt(splitDate[1]) - 1;
      const year = parseInt(splitDate[2]);

      return new Date(year, month, day);
    },
    mediumClass(noAnswers) {
      return noAnswers < 12 ? 'medium-2' : 'medium-1';
    },
    checkIfQuestionAnswerIsBlocking(question) {
      var selectedAnswer = question.AnswersList.find(
        (answer) => answer.Id == question.ChosenAnswer
      );
      if (selectedAnswer && selectedAnswer.Block) {
        this.isBlocked = true;
      }
    },
    emitUpdate(question, index) {
      if (question.AnswersList === undefined) {
        return;
      }

      let answer = this.returnAnswer(question, index);

      this.$emit('questionnaire-updated', {
        questionId: this.getQuestionId(question, index),
        answer: answer,
        blockStatus: this.isBlocked
      });
    },
    returnAnswer(question, index) {
      if (question.AnswerType === 10001) {
        return this.options[index].ChosenAnswer;
      }
      return question.AnswersList.find((elm) => elm.Id === question.ChosenAnswer);
    }
  }
};
</script>
